import type { SerializableRecord } from './contracts';

export type ProcessingErrorCode =
  | 'processing.cancelled'
  | 'processing.assetMissing'
  | 'processing.duplicateJob'
  | 'processing.invalidProtocol'
  | 'processing.invalidProgress'
  | 'processing.invalidCrop'
  | 'processing.invalidPagePlan'
  | 'processing.invalidPageRange'
  | 'processing.imageDecodeFailed'
  | 'processing.invalidPassword'
  | 'processing.malformedInput'
  | 'processing.outputInvalid'
  | 'processing.passwordRequired'
  | 'processing.processorMismatch'
  | 'processing.processorUnavailable'
  | 'processing.removeAllPages'
  | 'processing.storageUnavailable'
  | 'processing.unexpected'
  | 'processing.validationFailed'
  | 'processing.validationHookMissing'
  | 'processing.workerFailed'
  | 'processing.workerTerminated'
  | 'processing.wrongPassword';

export interface ProcessingFailure {
  readonly code: ProcessingErrorCode;
  readonly details?: SerializableRecord;
  readonly retriable: boolean;
}

export class ProcessingError extends Error {
  public constructor(
    public readonly code: ProcessingErrorCode,
    public readonly details?: SerializableRecord,
    public readonly retriable = false,
  ) {
    super(code);
    this.name = 'ProcessingError';
  }
}

export class ProcessingCancelledError extends ProcessingError {
  public constructor(public readonly hardTermination: boolean) {
    super(
      hardTermination ? 'processing.workerTerminated' : 'processing.cancelled',
      { hardTermination },
      true,
    );
    this.name = 'ProcessingCancelledError';
  }
}

export class RemoteProcessingError extends ProcessingError {
  public constructor(failure: ProcessingFailure) {
    super(failure.code, failure.details, failure.retriable);
    this.name = 'RemoteProcessingError';
  }
}

/** Converts unknown engine errors without copying their message, stack, file names, or data. */
export function serializeProcessingFailure(error: unknown): ProcessingFailure {
  if (error instanceof ProcessingError) {
    return {
      code: error.code,
      ...(error.details === undefined ? {} : { details: error.details }),
      retriable: error.retriable,
    };
  }
  return { code: 'processing.unexpected', retriable: false };
}
